import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SendIcon from '@mui/icons-material/Send';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LinkIcon from '@mui/icons-material/Link';
import FeedIcon from '@mui/icons-material/Feed';
import {
  ClosingDeadlineItemDto,
  ClosingDeferralRowDto,
  ClosingHomeResponseDto,
  ClosingOfferRowDto,
  ClosingResourceLinkDto,
  fetchClosingHome,
} from '../../api/ClosingHomeApi';
import AutomationActivityFeed from '../../components/AutomationActivityFeed/AutomationActivityFeed';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

const ManagerClosingHomeView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedStaff, setSelectedStaff] = useState<string>('all');
  const [selectedOfferStatus, setSelectedOfferStatus] = useState<string>('all');
  const [selectedDeferralStatus, setSelectedDeferralStatus] = useState<string>('all');
  const [deadlineWindowFilter, setDeadlineWindowFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');

  const [data, setData] = useState<ClosingHomeResponseDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  const loadClosingHome = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchClosingHome({
        staff: selectedStaff,
        offerStatus: selectedOfferStatus as any,
        deferralStatus: selectedDeferralStatus as any,
        search: appliedSearch || undefined,
      });
      setData(response);
    } catch (err) {
      console.error('Failed to load closing home:', err);
      setError(err instanceof Error ? err.message : 'Failed to load closing home data');
    } finally {
      setLoading(false);
    }
  }, [selectedStaff, selectedOfferStatus, selectedDeferralStatus, appliedSearch]);

  useEffect(() => {
    loadClosingHome();
  }, [loadClosingHome]);

  const handleCopyLink = async (url: string, title: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setSnackbarMessage(`Copied ${title} URL to clipboard!`);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setSnackbarMessage(`Failed to copy: ${url}`);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
  };

  const counts = data?.summaryCounts;

  // Filter deadlines by timeWindow
  const displayedDeadlines = (data?.deadlines ?? []).filter((d) => {
    if (deadlineWindowFilter === 'all') return true;
    if (deadlineWindowFilter === 'overdue') return d.timeWindow === 'overdue';
    if (deadlineWindowFilter === 'today') return d.timeWindow === 'today';
    if (deadlineWindowFilter === 'next_7_days') return d.timeWindow === 'today' || d.timeWindow === 'next_7_days';
    if (deadlineWindowFilter === 'next_30_days') {
      return d.timeWindow === 'today' || d.timeWindow === 'next_7_days' || d.timeWindow === 'next_30_days';
    }
    if (deadlineWindowFilter === 'future') return d.timeWindow === 'future';
    return true;
  });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Top Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        sx={{ mb: 2.5, gap: 1.5 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Closing & Compliance Department Home
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Plea offer conveying, deferral & DSC case compliance, client notices calendar, and approved compliance resources.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => loadClosingHome()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to="/activity-feed"
          >
            Automation Activity Feed
          </Button>
        </Stack>
      </Stack>

      {/* KPI Metric Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card elevation={1} sx={{ height: '100%', bgcolor: 'background.paper', borderLeft: '4px solid #ed6c02' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                DRAFTED OFFERS
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: '#ed6c02' }}>
                {counts?.offersDrafted ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Awaiting review & send
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card elevation={1} sx={{ height: '100%', bgcolor: 'background.paper', borderLeft: '4px solid #0288d1' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                CONVEYED OFFERS
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: '#0288d1' }}>
                {counts?.offersPendingResponse ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Awaiting client answer
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card elevation={1} sx={{ height: '100%', bgcolor: 'background.paper', borderLeft: '4px solid #2e7d32' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                ACCEPTED OFFERS
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: '#2e7d32' }}>
                {counts?.offersAccepted ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Terms accepted by client
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card elevation={1} sx={{ height: '100%', bgcolor: 'background.paper', borderLeft: '4px solid #3f51b5' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                DEFERRAL / DSC CASES
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: '#3f51b5' }}>
                {counts?.totalDeferralStageCases ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {counts?.deferralNoDeadlines ?? 0} with no deadlines
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card elevation={1} sx={{ height: '100%', bgcolor: 'background.paper', borderLeft: '4px solid #9c27b0' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                DEADLINES IN 7 DAYS
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: '#9c27b0' }}>
                {counts?.deadlinesNext7Days ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Conditions due soon
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card elevation={1} sx={{ height: '100%', bgcolor: 'background.paper', borderLeft: '4px solid #d32f2f' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                OVERDUE DEADLINES
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: '#d32f2f' }}>
                {counts?.deadlinesOverdue ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Court conditions overdue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<SendIcon fontSize="small" />}
            iconPosition="start"
            label={`Plea Offers (${counts?.totalOfferStageCases ?? 0})`}
          />
          <Tab
            icon={<AssignmentIcon fontSize="small" />}
            iconPosition="start"
            label={`Deferral / DSC Cases (${counts?.totalDeferralStageCases ?? 0})`}
          />
          <Tab
            icon={<AccessTimeIcon fontSize="small" />}
            iconPosition="start"
            label={`Client Deadlines (${counts?.totalActiveDeadlines ?? 0})`}
          />
          <Tab
            icon={<LinkIcon fontSize="small" />}
            iconPosition="start"
            label="Compliance Resources"
          />
          <Tab
            icon={<FeedIcon fontSize="small" />}
            iconPosition="start"
            label="Closing Automation Feed"
          />
        </Tabs>
      </Paper>

      {/* Search and Universal Filter Bar (shown on Offers & Deferrals tabs) */}
      {(activeTab === 0 || activeTab === 1) && (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              component="form"
              onSubmit={handleSearchSubmit}
              sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: '45%' } }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search by client, matter #, citation, court, or staff..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <Button type="submit" variant="contained" size="small">
                Search
              </Button>
              {appliedSearch && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSearchInput('');
                    setAppliedSearch('');
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>

            <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Staff (last contact)</InputLabel>
                <Select
                  value={selectedStaff}
                  label="Staff (last contact)"
                  onChange={(e) => setSelectedStaff(e.target.value)}
                >
                  <MenuItem value="all">All Staff</MenuItem>
                  {(data?.staffOptions ?? []).map((staffName) => (
                    <MenuItem key={staffName} value={staffName}>
                      {staffName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {activeTab === 0 && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Offer Status</InputLabel>
                  <Select
                    value={selectedOfferStatus}
                    label="Offer Status"
                    onChange={(e) => setSelectedOfferStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Offers</MenuItem>
                    <MenuItem value="drafted">Drafted / Awaiting Send</MenuItem>
                    <MenuItem value="conveyed">Conveyed (Pending Answer)</MenuItem>
                    <MenuItem value="accepted">Accepted</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="unrecorded">Offer Not Recorded</MenuItem>
                  </Select>
                </FormControl>
              )}

              {activeTab === 1 && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Conditions Filter</InputLabel>
                  <Select
                    value={selectedDeferralStatus}
                    label="Conditions Filter"
                    onChange={(e) => setSelectedDeferralStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Deferral Cases</MenuItem>
                    <MenuItem value="with_conditions">With Deadlines Recorded</MenuItem>
                    <MenuItem value="no_deadlines">No Deadlines Recorded</MenuItem>
                    <MenuItem value="overdue">Has Overdue Condition</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* TAB 0: PLEA OFFERS */}
          {activeTab === 0 && (
            <Paper elevation={1}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Case / Matter</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Court</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Case Stage</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Offer Terms</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Conveyance</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Client Response</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Last Contact</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Staff (last contact)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.offers ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No cases found matching the current filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (data?.offers ?? []).map((row) => (
                        <TableRow key={row.ticketGuid} hover>
                          <TableCell>
                            <Link
                              to={`/ticket/${row.ticketGuid}`}
                              style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 600 }}
                            >
                              {row.matterNumber || row.ticketNumber || 'Case'}
                            </Link>
                            {row.citationDescription && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {row.citationDescription}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {row.clientName}
                            </Typography>
                            {row.clientPhone && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {row.clientPhone}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">{row.courtName || '—'}</Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={row.statusName}
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              {row.daysInStage === 0 ? 'Entered stage today' : `${row.daysInStage}d in this stage`}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {row.hasOfferRecord ? (
                              <Box>
                                <Chip
                                  size="small"
                                  label={row.offerType ? row.offerType.toUpperCase() : 'OFFER'}
                                  color="primary"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                                {row.deferralDays != null && (
                                  <Typography variant="caption" display="block">
                                    {row.deferralDays}-day deferral
                                  </Typography>
                                )}
                                {row.deferralFeeUsd != null && (
                                  <Typography variant="caption" display="block">
                                    Fee: ${row.deferralFeeUsd}
                                  </Typography>
                                )}
                                {row.dscAlsoRequired && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    + DSC course required
                                  </Typography>
                                )}
                                {row.conditions && row.conditions.length > 0 && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    Conditions: {row.conditions.join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Chip
                                size="small"
                                label="Offer not recorded"
                                variant="outlined"
                                color="default"
                                sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                              />
                            )}
                          </TableCell>

                          <TableCell>
                            {row.conveyanceStatus === 'conveyed' ? (
                              <Tooltip title={`Conveyed on ${formatDate(row.conveyedAt)} via ${row.conveyedChannels || 'email'}`}>
                                <Chip
                                  size="small"
                                  label={`SENT (${row.conveyedChannels || 'email'})`}
                                  color="success"
                                  icon={<CheckCircleIcon />}
                                />
                              </Tooltip>
                            ) : row.conveyanceStatus === 'drafted' ? (
                              <Tooltip title="Offer recorded but not conveyed to client yet. Open case to review and send.">
                                <Chip
                                  size="small"
                                  label="DRAFTED (UNSENT)"
                                  color="warning"
                                  icon={<WarningAmberIcon />}
                                />
                              </Tooltip>
                            ) : (
                              <Chip size="small" label="UNRECORDED" variant="outlined" />
                            )}
                          </TableCell>

                          <TableCell>
                            {row.clientResponse === 'accepted' ? (
                              <Chip
                                size="small"
                                label="ACCEPTED"
                                color="success"
                                sx={{ fontWeight: 600 }}
                              />
                            ) : row.clientResponse === 'rejected' ? (
                              <Chip
                                size="small"
                                label="REJECTED"
                                color="error"
                                sx={{ fontWeight: 600 }}
                              />
                            ) : row.conveyanceStatus === 'conveyed' ? (
                              <Chip
                                size="small"
                                label="PENDING ANSWER"
                                color="info"
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                            {row.acceptanceSentAt && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                Acceptance sent {row.acceptanceSentAt}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {row.lastContactAt ? (
                              <Box>
                                <Typography variant="caption" display="block" sx={{ fontWeight: 500 }}>
                                  {row.lastContactBy || 'Staff'} ({row.daysSinceLastContact}d ago)
                                </Typography>
                                {row.lastContactSummary && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      maxWidth: 160,
                                    }}
                                  >
                                    {row.lastContactSummary}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No contact yet
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.assignedStaffName || '—'}
                            </Typography>
                            {row.assignedStaffSource && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                ({row.assignedStaffSource === 'assigned' ? 'Assigned' : 'Last contact'})
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* TAB 1: DEFERRAL / DSC CASES */}
          {activeTab === 1 && (
            <Paper elevation={1}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Case / Matter</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Court</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Deferral Timeline</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Recorded Conditions & Deadlines</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Next Deadline</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Last Contact</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Staff (last contact)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.deferrals ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No deferral / DSC cases found matching the current filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (data?.deferrals ?? []).map((row) => (
                        <TableRow key={row.ticketGuid} hover>
                          <TableCell>
                            <Link
                              to={`/ticket/${row.ticketGuid}`}
                              style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 600 }}
                            >
                              {row.matterNumber || row.ticketNumber || 'Case'}
                            </Link>
                            {row.citationDescription && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {row.citationDescription}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {row.clientName}
                            </Typography>
                            {row.clientPhone && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {row.clientPhone}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">{row.courtName || '—'}</Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={row.deferralSummary}
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                            {row.deferralDays != null && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                Terms: {row.deferralDays}-day deferral
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {row.hasConditionsRecorded ? (
                              <Stack spacing={0.5}>
                                {row.conditions.map((cond) => (
                                  <Box key={cond.conditionGuid} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Chip
                                      size="small"
                                      label={`${cond.label}: ${cond.dueAt || 'Undated'}`}
                                      color={cond.completedAt ? 'success' : cond.isOverdue ? 'error' : 'default'}
                                      variant={cond.completedAt ? 'filled' : 'outlined'}
                                      icon={cond.isOverdue ? <ErrorOutlineIcon /> : undefined}
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                    {cond.completedAt && (
                                      <Typography variant="caption" color="success.main">
                                        (Done {cond.completedAt})
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Tooltip title="No condition deadlines recorded for this case yet. Click case to add conditions from the court order.">
                                <Chip
                                  size="small"
                                  label="No deadlines recorded"
                                  variant="outlined"
                                  color="warning"
                                  sx={{ fontStyle: 'italic' }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>

                          <TableCell>
                            {row.nextDeadlineAt ? (
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: row.hasOverdueDeadline ? 'error.main' : 'text.primary',
                                  }}
                                >
                                  {row.nextDeadlineAt}
                                </Typography>
                                {row.hasOverdueDeadline && (
                                  <Typography variant="caption" color="error" display="block">
                                    Condition overdue
                                  </Typography>
                                )}
                              </Box>
                            ) : row.nextCourtDate ? (
                              <Typography variant="caption" color="text.secondary">
                                Court setting: {row.nextCourtDate}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {row.lastContactAt ? (
                              <Box>
                                <Typography variant="caption" display="block" sx={{ fontWeight: 500 }}>
                                  {row.lastContactBy || 'Staff'} ({row.daysSinceLastContact}d ago)
                                </Typography>
                                {row.lastContactSummary && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      maxWidth: 160,
                                    }}
                                  >
                                    {row.lastContactSummary}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No contact yet
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.assignedStaffName || '—'}
                            </Typography>
                            {row.assignedStaffSource && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                ({row.assignedStaffSource === 'assigned' ? 'Assigned' : 'Last contact'})
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* TAB 2: CLIENT DEADLINES CALENDAR / TIMELINE */}
          {activeTab === 2 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Showing recorded client conditions from cases in Deferral / DSC status.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Deadlines appear on this timeline as staff enter court conditions (DSC course, driving record, fine payments, affidavits) on individual cases.
                </Typography>
              </Alert>

              {/* Time window filter */}
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={`All Deadlines (${data?.deadlines.length ?? 0})`}
                  color={deadlineWindowFilter === 'all' ? 'primary' : 'default'}
                  onClick={() => setDeadlineWindowFilter('all')}
                  clickable
                />
                <Chip
                  label={`Overdue (${counts?.deadlinesOverdue ?? 0})`}
                  color={deadlineWindowFilter === 'overdue' ? 'error' : 'default'}
                  onClick={() => setDeadlineWindowFilter('overdue')}
                  clickable
                />
                <Chip
                  label="Due Today"
                  color={deadlineWindowFilter === 'today' ? 'warning' : 'default'}
                  onClick={() => setDeadlineWindowFilter('today')}
                  clickable
                />
                <Chip
                  label={`Next 7 Days (${counts?.deadlinesNext7Days ?? 0})`}
                  color={deadlineWindowFilter === 'next_7_days' ? 'primary' : 'default'}
                  onClick={() => setDeadlineWindowFilter('next_7_days')}
                  clickable
                />
                <Chip
                  label={`Next 30 Days (${counts?.deadlinesNext30Days ?? 0})`}
                  color={deadlineWindowFilter === 'next_30_days' ? 'primary' : 'default'}
                  onClick={() => setDeadlineWindowFilter('next_30_days')}
                  clickable
                />
                <Chip
                  label="Future"
                  color={deadlineWindowFilter === 'future' ? 'default' : 'default'}
                  onClick={() => setDeadlineWindowFilter('future')}
                  clickable
                />
              </Stack>

              <Paper elevation={1} sx={{ mb: 3 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Obligation / Condition</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Client & Case</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Court</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Amount / Detail</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedDeadlines.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No active condition deadlines found for this timeframe.
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayedDeadlines.map((dl) => (
                          <TableRow key={dl.conditionGuid} hover>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: dl.timeWindow === 'overdue' ? 'error.main' : 'text.primary',
                                }}
                              >
                                {dl.dueAt}
                              </Typography>
                              <Chip
                                size="small"
                                label={dl.timeWindow === 'overdue' ? 'OVERDUE' : dl.timeWindow === 'today' ? 'DUE TODAY' : dl.timeWindow}
                                color={dl.timeWindow === 'overdue' ? 'error' : dl.timeWindow === 'today' ? 'warning' : 'default'}
                                sx={{ fontSize: '0.7rem', height: 20, mt: 0.5 }}
                              />
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {dl.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Type: {dl.type}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {dl.clientName}
                              </Typography>
                              <Link
                                to={`/ticket/${dl.ticketGuid}`}
                                style={{ textDecoration: 'none', color: '#1976d2', fontSize: '0.8rem' }}
                              >
                                {dl.matterNumber || dl.ticketNumber || 'View Case'}
                              </Link>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">{dl.courtName || '—'}</Typography>
                            </TableCell>

                            <TableCell>
                              {dl.amountCents != null && (
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatCents(dl.amountCents)}
                                </Typography>
                              )}
                              {dl.hours != null && (
                                <Typography variant="body2">{dl.hours} hours</Typography>
                              )}
                              {dl.detail && (
                                <Typography variant="caption" color="text.secondary">
                                  {dl.detail}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              {dl.completedAt ? (
                                <Chip size="small" label="Completed" color="success" />
                              ) : dl.timeWindow === 'overdue' ? (
                                <Chip size="small" label="Overdue" color="error" />
                              ) : (
                                <Chip size="small" label="Pending" color="info" variant="outlined" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Undated Conditions section */}
              {(data?.undatedConditions ?? []).length > 0 && (
                <Paper elevation={1} sx={{ p: 2, borderLeft: '4px solid #ed6c02' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#ed6c02', mb: 1 }}>
                    Undated Conditions ({data?.undatedConditions.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    These conditions exist on open cases without a due date. Staff should review the court order and enter the deadline.
                  </Typography>
                  <Grid container spacing={1.5}>
                    {(data?.undatedConditions ?? []).map((uc) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={uc.conditionGuid}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {uc.label} — {uc.clientName}
                          </Typography>
                          <Link
                            to={`/ticket/${uc.ticketGuid}`}
                            style={{ textDecoration: 'none', color: '#1976d2', fontSize: '0.8rem' }}
                          >
                            Case: {uc.ticketNumber || uc.ticketGuid.slice(0, 8)}
                          </Link>
                          {uc.detail && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {uc.detail}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}
            </Box>
          )}

          {/* TAB 3: COMPLIANCE RESOURCES & LINKS */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Approved External Compliance & Course Portals
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                One-click links to copy or send to clients for defensive driving courses and certified driving records.
              </Typography>

              <Grid container spacing={2.5}>
                {(data?.resourceLinks ?? []).map((resource) => (
                  <Grid size={{ xs: 12, md: 6 }} key={resource.url}>
                    <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={resource.category === 'dsc' ? 'DRIVING SAFETY COURSE' : 'DRIVING RECORD'}
                            color={resource.category === 'dsc' ? 'primary' : 'secondary'}
                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        </Stack>

                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          {resource.title}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {resource.description}
                        </Typography>

                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: 'action.hover',
                            wordBreak: 'break-all',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {resource.url}
                          </Typography>
                          <Tooltip title="Copy link">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyLink(resource.url, resource.title)}
                              sx={{ ml: 1 }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Paper>

                        <Stack direction="row" spacing={1.5}>
                          <Button
                            variant="contained"
                            size="small"
                            endIcon={<OpenInNewIcon />}
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open {resource.buttonLabel}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => handleCopyLink(resource.url, resource.title)}
                          >
                            Copy Link
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* TAB 4: CLOSING AUTOMATION FEED */}
          {activeTab === 4 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Closing Department Automation Stream
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Live audit feed of offer conveys, court acceptances, and condition reminders.
              </Typography>
              <AutomationActivityFeed defaultDepartment="closing" compact={false} />
            </Box>
          )}
        </>
      )}

      {/* Snackbar feedback */}
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default ManagerClosingHomeView;
